import { backendApi } from "../backendApi";
import {
  transformBackendUser,
  type IBackendUser,
  type IBackendUserQueryParams,
  type IPaginatedUserResponse,
  type IBackendUserApiResponse,
  type ICreateUserRequest,
  type IRole,
  type IUserType,
  type IUserAndAccessMgmtAuditLog,
  type IUserLoggingActivityQueryParams,
  type IUserLoggingActivityResponse,
} from "./type";

type IUserAndAccessMgmtAuditLogRequest = {
  pageSize?: number;
  pageNumber?: number;
  userEmail?: string;
};

type IUserAndAccessMgmtAuditLogApiResponse = {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  data: IUserAndAccessMgmtAuditLog[];
};

export const backendUserApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllUsersFromBackend: builder.query<
      IPaginatedUserResponse,
      Partial<IBackendUserQueryParams>
    >({
      query: (params = {}) => {
        // Build request body with all optional parameters and defaults
        // Note: correlationId is automatically injected by backendBaseQuery
        const requestBody = {
          pageSize: params.pageSize || 10, // Default to smaller page size for better performance
          pageNumber: params.pageNumber || 1,
          email: params.email || "",
          roleId: params.roleId !== undefined ? params.roleId : 0,
          userTypeId: params.userTypeId !== undefined ? params.userTypeId : 0,
          givenName: params.givenName || "",
          isActive: params.isActive,
        };

        return {
          url: "/User/GetAllUsers",
          method: "POST",
          body: requestBody,
        };
      },
      transformResponse: (response: unknown, _meta, arg) => {
        // Handle case where response might be null or invalid
        if (!response || typeof response !== "object") {
          return {
            data: [],
            total: 0,
            page: arg.pageNumber || 1, // Use 1-based pagination directly
            limit: arg.pageSize || 10,
            totalPages: 0,
          };
        }

        const apiResponse = response as IBackendUserApiResponse;

        // Validate the expected structure
        if (!apiResponse.data || !Array.isArray(apiResponse.data)) {
          return {
            data: [],
            total: 0,
            page: arg.pageNumber || 1, // Use 1-based pagination directly
            limit: arg.pageSize || 10,
            totalPages: 0,
          };
        }

        // Transform backend response to frontend format
        const transformedData = apiResponse.data.map(transformBackendUser);

        // Use pagination metadata from the API response
        // Both frontend and API now use 1-based pagination
        const result = {
          data: transformedData,
          total: apiResponse.totalRecords,
          page: apiResponse.page, // Use 1-based pagination directly
          limit: apiResponse.pageSize,
          totalPages: apiResponse.totalPages,
        };

        return result;
      },
      providesTags: ["AllUsers"],
    }),
    getLoggedInUserInfo: builder.query<IBackendUser, void>({
      query: () => ({
        url: "/User/GetLoggedInUserInfo",
        method: "POST",
      }),
      providesTags: ["LoggedInUser"],
    }),
    createUser: builder.mutation<IBackendUser, ICreateUserRequest>({
      query: (createUserRequest) => ({
        url: "/User/CreateUser",
        method: "POST",
        body: {
          createUserRequest,
          // correlationId is automatically injected by backendBaseQuery
        },
      }),
      invalidatesTags: ["AllUsers", "LoggedInUser"],
    }),
    getAllRoles: builder.query<IRole[], void>({
      query: () => ({
        url: "/User/GetAllRoles",
        method: "POST",
        body: {
          // correlationId is automatically injected by backendBaseQuery
        },
      }),
    }),
    getAllUserTypes: builder.query<IUserType[], void>({
      query: () => ({
        url: "/User/GetAllUserTypes",
        method: "POST",
        body: {
          // correlationId is automatically injected by backendBaseQuery
        },
      }),
    }),
    updateUser: builder.mutation<
      IBackendUser,
      {
        userEmail: string;
        roleId: number;
        userTypeId: number;
        isActive: boolean | null;
        modifiedBy: string;
      }
    >({
      query: ({ userEmail, roleId, userTypeId, isActive, modifiedBy }) => ({
        url: "/User/UpdateUser",
        method: "PUT",
        body: {
          editUserRequest: {
            userEmail,
            roleId,
            userTypeId,
            isActive,
            modifiedBy,
          },
        },
      }),
      invalidatesTags: ["AllUsers", "LoggedInUser"],
    }),
    getUserLoggingActivityInfo: builder.query<
      IUserLoggingActivityResponse,
      Partial<IUserLoggingActivityQueryParams>
    >({
      query: (params = {}) => ({
        url: "/User/UserLoggingActivityInfo",
        method: "POST",
        body: {
          pageSize: params.pageSize ?? 10,
          pageNumber: params.pageNumber ?? 1,
          userEmail: params.userEmail ?? "",
          // correlationId is automatically injected by backendBaseQuery
        },
      }),
    }),
    getUserAndAccessMgmtAuditLogInfo: builder.query<
      IUserAndAccessMgmtAuditLogApiResponse,
      IUserAndAccessMgmtAuditLogRequest
    >({
      query: (params = {}) => {
        const requestBody = {
          pageSize: params.pageSize ?? 10,
          pageNumber: params.pageNumber ?? 1,
          userEmail: params.userEmail ?? "",
        };

        return {
          url: "/User/UserAndAccessMgmtAuditLogInfo",
          method: "POST",
          body: requestBody,
        };
      },
    }),
  }),
});

export const {
  useGetAllUsersFromBackendQuery,
  useGetLoggedInUserInfoQuery,
  useCreateUserMutation,
  useGetAllRolesQuery,
  useGetAllUserTypesQuery,
  useUpdateUserMutation,
  useGetUserLoggingActivityInfoQuery,
  useLazyGetUserLoggingActivityInfoQuery,
  useGetUserAndAccessMgmtAuditLogInfoQuery,
  useLazyGetUserAndAccessMgmtAuditLogInfoQuery,
} = backendUserApi;
